import { createContext, useContext } from "react";
import type * as React from "react";

// The shared item-menu's context and the few things code outside
// `icon-menu-button.tsx` needs from it — a trigger, and `AppDialog` resolving
// a menu row to its trigger. Its own module because a file that
// exports a non-component alongside its components breaks fast refresh —
// the same reason `menu-trigger-press.ts` sits beside it rather than inside
// it.

// How a menu was opened — by pointer, or by key — recorded by the trigger on
// itself, under this attribute, and read back by the content it opens.
//
// On the trigger's own DOM node rather than in a variable, because the trigger
// and the content are siblings under a `DropdownMenu` the *caller* writes: no
// React state reaches from one to the other without a provider threaded through
// every call site. The DOM already carries the link — Radix gives the content
// `aria-labelledby={triggerId}` and the trigger that same `id` — so the answer
// travels the way the accessibility tree already does, per menu, with nothing
// to go stale between two menus on one page (#457, cross-review).
//
// Reading `document.activeElement` in the content's effect is what this
// replaced, and it does not work: by then Radix has focused the content, and
// Chromium reports a programmatically focused `div` as `:focus-visible`. That
// read answered "keyboard" for every mouse open — the exact wash it exists to
// suppress. It passed the browser suite and lit up every menu in the real app.
export const OPENED_BY = "data-opened-by";

/**
 * Lets the content close the menu its caller opened.
 *
 * The menu is controlled by `IconMenuButton` rather than left to Radix's own
 * state for one reason: `Escape`. Radix gives every dismissable layer the same
 * guard — `index === context.layers.size - 1` (`react-dismissable-layer/
 * dist/index.mjs:59-67`) — so a menu with a blocked row's reason showing is no
 * longer the highest layer and its own handler stands down. `onEscapeKeyDown`
 * on the content is not a way round it: that prop is called from inside the
 * same guard. Closing therefore has to come from outside Radix's layer stack,
 * which means the open state has to be ours.
 */
export const IconMenuContext = createContext<{
  open: boolean;
  close: () => void;
  /**
   * Flips the menu open or shut — what the trigger calls from its `click`,
   * where both donors flip theirs (`menu-button.tsx:75`,
   * `ActionMenu.tsx:36-39`). It has to come from here for the same reason
   * `close` does: the state is ours, not Radix's.
   */
  toggle: () => void;
  /**
   * Hands the paper to the root, so it can read the paper's live state at the
   * moment it closes. A callback rather than the ref itself: a ref passed down
   * here would be written through the context value, and context values are
   * not ours to mutate.
   */
  registerContent: (node: HTMLDivElement | null) => void;
  /**
   * How many times the menu has opened. The content keys its first-row focus
   * on it, so a menu reopened while its paper is still growing out — the same
   * mounted content, which Radix's focus scope never auto-focuses twice — lands
   * on its first row again, as MUI's `MenuList` does on every opening.
   */
  openings: number;
} | null>(null);

/**
 * The menu state a trigger needs: `toggle`, which is the only path that runs
 * `pinExitStart` on the way out, and — via the helper below — the modality it
 * must record.
 *
 * `IconMenuButtonTrigger` (`icon-menu-button.tsx`) is the sole caller, and the
 * trigger both screens render. It took a `composedBy` argument while the
 * catalog batch toolbar's gear composed a second trigger of its own; that
 * trigger is gone, so the message names the one caller outright rather than
 * carrying a parameter with a single literal argument.
 */
export function useIconMenu() {
  const menu = useContext(IconMenuContext);
  if (!menu) {
    throw new Error(
      "IconMenuButtonTrigger must be composed under <IconMenuButton>, not a bare <DropdownMenu>.",
    );
  }
  return menu;
}

/**
 * Records on the trigger's own element how it was pressed, which is what
 * `IconMenuButtonContent` reads to decide whether the row it focuses on open
 * carries the keyboard wash.
 *
 * On the element rather than in a module variable because a catalog page holds
 * one of these menus per item, and a shared variable answers the *next* menu
 * with the previous one's modality. Both triggers use this; a trigger that
 * records nothing gets the loud wash on every mouse open, which is #457.
 */
export function iconMenuModalityProps<E extends HTMLElement>(): {
  onPointerDown: React.PointerEventHandler<E>;
  onKeyDown: React.KeyboardEventHandler<E>;
} {
  return {
    onPointerDown: (event) => event.currentTarget.setAttribute(OPENED_BY, "pointer"),
    onKeyDown: (event) => event.currentTarget.setAttribute(OPENED_BY, "keyboard"),
  };
}

/**
 * The trigger a menu's content hangs from.
 *
 * Through the DOM rather than a ref, because the trigger and the content are
 * siblings under a `DropdownMenu` the *caller* writes: no React state reaches
 * from one to the other without a provider threaded through every call site.
 * Radix already links them for the accessibility tree — the content carries
 * `aria-labelledby={triggerId}` and the trigger that same `id` — so the answer
 * travels the way that link does, per menu, with nothing to go stale between
 * two menus on one page (#457, cross-review).
 */
export function triggerOf(content: HTMLElement) {
  return document.getElementById(content.getAttribute("aria-labelledby") ?? "");
}
